import React, {
  FC, FormEvent, SyntheticEvent, useEffect, useState,
} from 'react';
import { FormikValues } from 'formik';
import { boolean, string } from 'yup';
import AutoCompleteInput from '../inputs/autoCompleteInput';
import { TRACKING_SOURCE_OPTIONS } from '../../constants/pageIdentifiers';
import { TrackingSourceInterface } from '../../interfaces/trackingSource.interface';
import { useGetTrackingSourceOptions } from '../../apiHooks/queries/adManagement/useGetTrackingSources';

interface TrackingSourceSelectInterface {
  data?: TrackingSourceInterface,
  formik?: FormikValues,
  fullWidth?: boolean,
  size?: 'small' | 'medium',
  setSelectedTrackingSourceId?: (_newSelectedTrackingSourceId: string) => void,
  setSelectedTrackingSource?: (_newSelectedTrackingSource: TrackingSourceInterface | null) => void,
  disabled?: boolean,
  hideDirect?: boolean,
  label: string,
  placeholder: string
}

const TrackingSourceSelect: FC<TrackingSourceSelectInterface> = (props) => {
  const [
    selectedValue,
    setSelectedValue,
  ] = useState<TrackingSourceInterface | null>(props.data || null);
  const [searchedValue, setSearchedValue] = useState<string>('');

  const {
    data: trackingSourcesOptionsData,
    refetch: refetchTrackingSourcesOptionsData,
    isFetching: isRefetchingTrackingSourcesOptionsData,
  } = useGetTrackingSourceOptions(TRACKING_SOURCE_OPTIONS, searchedValue);

  useEffect(() => {
    if (props.data) {
      setSelectedValue(props.data);
    }
  }, [props.data]);
  // TODO: check if this useEffect is remotely needed

  const onChangeAutoComplete = (event: FormEvent, newValue: TrackingSourceInterface) => {
    if (newValue?.id) {
      setSelectedValue(newValue);
      props.formik?.setFieldValue?.('trackingSourceId', newValue.id);
      props.setSelectedTrackingSourceId?.(JSON.stringify(newValue.id));
      props.setSelectedTrackingSource?.(newValue);
      setSearchedValue('');
    } else {
      setSelectedValue(null);
      props.formik?.setFieldValue?.('trackingSourceId', '');
      props.setSelectedTrackingSourceId?.('');
      props.setSelectedTrackingSource?.(null);
      setSearchedValue('');
    }
  };

  const onInputChangeAutoComplete = async (event: SyntheticEvent, value: string) => {
    setSelectedValue(null);
    setSearchedValue(value );
  };

  const options = (trackingSourcesOptionsData?.data ? trackingSourcesOptionsData.data : []).filter(
    (opt: TrackingSourceInterface) => {
      if (!props.hideDirect) return true;
      const name = (opt?.name ?? '').toString().trim().toLowerCase();
      return !(opt?.id === 2 || name === 'direct');
    },
  );

  return (
    <AutoCompleteInput
      key="trackingSourceId"
      name="trackingSourceId"
      label={props.label}
      placeholder={props.label}
      size={props.size}
      style={{ minWidth: 190 }}
      options={options}
      getOptionLabel={(option: TrackingSourceInterface) => option?.name || ''}
      value={selectedValue}
      loading={isRefetchingTrackingSourcesOptionsData}
      onChange={(event: FormEvent, newValue: TrackingSourceInterface) => onChangeAutoComplete(event, newValue)}
      inputValue={selectedValue?.name || searchedValue}
      onInputChange={async (event: FormEvent, value: string) => {
        await onInputChangeAutoComplete(event, value);
      }}
      isOptionEqualToValue={(option: TrackingSourceInterface, value: TrackingSourceInterface) => (
        option.id === value.id
      )}
      error={Boolean(props.formik?.touched.trackingSourceId) && Boolean(props.formik?.errors.trackingSourceId)}
      helperText={Boolean(props.formik?.touched.trackingSourceId) && props.formik?.errors.trackingSourceId}
      fullWidth={props.fullWidth}
      disabled={props.disabled}
    />
  );
};

export default TrackingSourceSelect;
